import { createSelector } from 'reselect';
import i18n from 'invision-core/src/components/i18n/i18n';
import { CODES } from 'invision-core/src/components/metadata/codes/codes.constants';
import { MetadataCodeTypeSelector } from 'invision-core/src/components/metadata/codes/codes.selectors';
import { CurrentBusinessUnitSelector } from 'invision-core/src/components/session/businessunit.selectors';
import { DiscountsMetadataMapSelector } from 'invision-core/src/components/metadata/discounts/discounts.selectors';
import { getFilterService } from 'invision-core/src/components/injectables/injector.helper';
import { createImmutableSelector } from 'invision-core/src/utilities/create.immutable.selector';
import { SelectedCustomerSelector } from './customer.selectors';
import CustomerCareKeys from '../../locales/keys';
import { deviceFinancingFriendlyName } from '../../components/customer/dashboard/dbssDashboard/deviceFinancing/device.financing.helper';
import { getSuspensionTypeByCode } from './services.list.selectors.helper';
var recoverableUIStateSelector = function recoverableUIStateSelector(state) {
  return state.customercare.recoverableUiState.devices;
};
var DEFAULT_DEVICES_MAP = {};
var CurrentDevicesMapSelector = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return selectedCustomer.devices.data ? selectedCustomer.devices.data : DEFAULT_DEVICES_MAP;
});
export var CurrentDevicesSelector = createSelector([CurrentDevicesMapSelector], function (currentDevicesMap) {
  return Object.keys(currentDevicesMap).map(function (key) {
    return currentDevicesMap[key];
  });
});
export var DeviceErrorsSelector = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return selectedCustomer.devices.devicesError;
});
var DEFAULT_ERRORS = [];
export var DeviceFormErrorSelector = createSelector([DeviceErrorsSelector], function (deviceError) {
  return deviceError && deviceError.message ? [deviceError.message] : DEFAULT_ERRORS;
});
export var DeviceFormSelector = createSelector([recoverableUIStateSelector], function (recoverable) {
  return recoverable.createDeviceModel.asMutable();
});
export var SelectedDeviceSelector = createSelector([recoverableUIStateSelector], function (recoverable) {
  return recoverable.selectedDevice;
});
var DEFAULT_DEVICES = [];
export var FormattedDevicesSelector = createSelector([CurrentDevicesSelector, MetadataCodeTypeSelector(CODES.DeviceType), MetadataCodeTypeSelector(CODES.PhysicalDeviceType)], function (devices, deviceTypes, physicalDeviceTypes) {
  if (deviceTypes.length > 0 && physicalDeviceTypes.length > 0) {
    return devices.map(function (device) {
      return formatDevice(device, deviceTypes, physicalDeviceTypes);
    });
  }
  return DEFAULT_DEVICES;
});
var formatDevice = function formatDevice(device, deviceTypes, physicalDeviceTypes) {
  var selectedDevice = deviceTypes.find(function (deviceType) {
    return parseInt(deviceType.Value, 10) === device.DeviceType;
  });
  var selectedPhysicalDevice = physicalDeviceTypes.find(function (physicalDeviceType) {
    return parseInt(physicalDeviceType.Value, 10) === device.PhysicalDeviceType;
  });
  return device.set('deviceTypeName', selectedDevice.Name).set('physicalDeviceTypeName', selectedPhysicalDevice.Name);
};
export var FormattedSelectedDeviceSelector = createSelector([FormattedDevicesSelector, SelectedDeviceSelector], function (devices, selectedDevice) {
  if (devices.length && selectedDevice) {
    return devices.find(function (device) {
      return device.uniqueId === selectedDevice.uniqueId || !selectedDevice.Start && device.Active && device.Id === selectedDevice.Id && device.DeviceId === selectedDevice.DeviceId;
    });
  }
  return null;
});
export var IncludeRemovedSelector = createSelector([recoverableUIStateSelector], function (uiState) {
  return uiState.filterData.includeRemoved;
});
export var IsRegisteringDevicesSelector = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return selectedCustomer.devices.isRegisteringDevice;
});
export var IsFetchingDevicesSelector = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return selectedCustomer.devices.isFetchingDevices;
});
export var IsRemovingDeviceSelector = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return selectedCustomer.devices.isRemovingDevice;
});
export var IsUpdatingDevicesSelector = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return selectedCustomer.devices.isUpdatingDevice;
});
export var RemainingDeviceAssociations = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return selectedCustomer.devices.remainingAssociations;
});
export var SelectedCustomerDevicesSelector = createImmutableSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return selectedCustomer.devices;
});
export var IsFetchingDeviceFinancingDetailsSelector = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return selectedCustomer.devices.isFetchingDeviceFinancingDetails;
});
export var IsFetchingDeviceReturnDetailsSelector = createImmutableSelector([SelectedCustomerDevicesSelector], function (selectedCustomerDevices) {
  return selectedCustomerDevices.isFetchingDeviceReturnDetails;
});
export var IsApplyingReturnDeviceAdjustmentSelector = createImmutableSelector([SelectedCustomerDevicesSelector], function (selectedCustomerDevices) {
  return selectedCustomerDevices.isApplyingDeviceAdjustment;
});
export var SubscriberDeviceFinancingCurrencySelector = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return selectedCustomer.devices.deviceFinancingCurrency;
});
export var SubscriberDeviceFinancingDetailsSelector = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return selectedCustomer.devices.deviceFinancingDetails;
});
export var SubscriberDeviceReturnDetailsSelector = createImmutableSelector([SelectedCustomerDevicesSelector], function (selectedCustomerDevices) {
  return selectedCustomerDevices.deviceReturnDetails;
});
export var SubscriberDeviceFinancingWidgetActiveDeviceSelector = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return selectedCustomer.devices.deviceFinancingWidgetSelectedDeviceIndex;
});
export var SubscriberDeviceFinancingWidgetActiveDeviceDetailsSelector = createSelector([SubscriberDeviceFinancingDetailsSelector, SubscriberDeviceFinancingWidgetActiveDeviceSelector], function (deviceFinancingDetails, selectedIndex) {
  var selectedDeviceFinancingDetails = deviceFinancingDetails && deviceFinancingDetails[selectedIndex];
  return selectedDeviceFinancingDetails && Object.assign({}, selectedDeviceFinancingDetails, {
    suspendType: selectedDeviceFinancingDetails.SubscriberInventory && getSuspensionTypeByCode(selectedDeviceFinancingDetails.SubscriberInventory.SuspendTypeCode),
    suspendedDate: selectedDeviceFinancingDetails.SubscriberInventory && getFilterService()('date')(selectedDeviceFinancingDetails.SubscriberInventory.SuspendedOn, 'shortDate')
  });
});
export var SubscriberDeviceFinancingWidgetActiveDiscountDetailsSelector = createImmutableSelector([SubscriberDeviceFinancingWidgetActiveDeviceDetailsSelector, DiscountsMetadataMapSelector, SubscriberDeviceFinancingCurrencySelector], function (deviceFinancingDetails, allDiscounts, currency) {
  var discounts = [];
  if (allDiscounts && deviceFinancingDetails && deviceFinancingDetails.FinanceDiscounts) {
    discounts = deviceFinancingDetails.FinanceDiscounts.map(function (financeDiscount) {
      if (allDiscounts[financeDiscount.DiscountId]) {
        return {
          Name: allDiscounts[financeDiscount.DiscountId].StorefrontText || allDiscounts[financeDiscount.DiscountId].Name,
          Currency: currency,
          savings: financeDiscount.Amount
        };
      }
    });
  }
  return discounts.filter(function (discount) {
    return discount;
  });
});
export var SubscriberDeviceFinancingDetailsDropdownSelector = createSelector([SubscriberDeviceFinancingDetailsSelector, SubscriberDeviceFinancingWidgetActiveDeviceSelector], function (deviceFinancingDetails, selectedIndex) {
  return deviceFinancingDetails.map(function (device, index) {
    return {
      text: deviceFinancingFriendlyName(device),
      selected: index === selectedIndex,
      index: index
    };
  });
});
export var DeviceFinancingNameSelector = createSelector([CurrentBusinessUnitSelector], function (businessUnit) {
  return businessUnit.deviceFinancingMarketingText || i18n.translate(CustomerCareKeys.CUSTOMER_DASHBOARD.DEVICE_FINANCING);
});